import { NotificationBell } from '../hub/NotificationBell';
import { beginNavigation } from '../../shared/navigation';
import { AccountLoading } from '../../shared/cart-widget';
import { ThemePicker } from '../../shared/preferences';
import { CartButton } from '../../shared/cart-widget';
import { PlatformCartProvider } from './PlatformCart';
import { FOOTER_CATEGORIES, PRODUCTS } from './products';
import AppsIcon from '@mui/icons-material/Apps';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { platformHubUrl } from '../../core/config';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LoginIcon from '@mui/icons-material/Login';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ROUTES } from '../../app/routes';
import { useOptionalAppTheme } from '../../main/theme/AppThemeContext';
import { useAppSelector } from '../../app/store';
import { publicApi, type JobCard, type OrganizationCard, type ProductCard, type Suggestions } from './publicApi';
import './public.css';

export const HUB_LANGUAGES = ['al', 'en', 'it'] as const;

/** Current UI language mapped onto the API's al/en/it set. */
export function usePublicLanguage(): string {
  const { i18n } = useTranslation();
  const lang = (i18n?.language ?? 'al').slice(0, 2);
  return (HUB_LANGUAGES as readonly string[]).includes(lang) ? lang : 'al';
}

const ICONS: Record<string, typeof StorefrontIcon> = {
  Restaurant: RestaurantIcon,
  Spa: SpaIcon,
  Hotel: HotelIcon,
  LocalPharmacy: LocalPharmacyIcon,
  CarRepair: CarRepairIcon,
};

/** `directory.Categories.Icon` holds an MUI icon name; unknown names fall back to a storefront. */
export function CategoryIcon({ name, size = 20 }: { name: string | null | undefined; size?: number }) {
  const Icon = (name && ICONS[name]) || StorefrontIcon;
  return <Icon sx={{ fontSize: size }} aria-hidden />;
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');
}

export function formatMoney(value: number | null, currency: string | null, lang: string) {
  if (value == null) return null;
  try {
    return new Intl.NumberFormat(lang === 'al' ? 'sq-AL' : lang, { style: currency ? 'currency' : 'decimal', currency: currency ?? undefined, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value} ${currency ?? ''}`.trim();
  }
}

export function companyPath(slug: string) {
  return ROUTES.company.replace(':slug', encodeURIComponent(slug));
}

export function openCompany(event: React.MouseEvent, slug: string) {
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  void slug;
  beginNavigation(event.currentTarget);
}

export function OrganizationCardView({ card, compact = false }: { card: OrganizationCard; compact?: boolean }) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const facts = [
    card.hasOnlineBooking ? t('hub.card.bookOnline', 'Book online') : null,
    card.hasShop ? t('hub.card.shop', 'Shop') : null,
    card.minPrice != null ? t('hub.card.from', 'from {{price}}', { price: formatMoney(card.minPrice, card.currencyCode, lang) }) : null,
    card.openJobs > 0 ? t('hub.card.jobs', '{{count}} open jobs', { count: card.openJobs }) : null,
  ].filter(Boolean);
  const subtitle = [card.primaryCategory?.name, card.city, card.distanceKm != null ? `${card.distanceKm < 1 ? Math.round(card.distanceKm * 1000) + ' m' : card.distanceKm.toFixed(1) + ' km'}` : null]
    .filter(Boolean).join(' · ');

  return <article className="hub-card">
    {!compact && <div className="hub-cover" style={card.coverUrl ? { backgroundImage: `url(${platformHubUrl(card.coverUrl)})` } : undefined}>
      {!card.coverUrl && <span className="initials">{initials(card.name)}</span>}
    </div>}
    <div className="pad">
      <div className="row">
        {card.logoUrl && <img src={platformHubUrl(card.logoUrl)} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />}
        <h3>
          <Link to={companyPath(card.slug)} onClick={(event) => openCompany(event, card.slug)}>{card.name}</Link>
          {card.isVerified && <VerifiedIcon titleAccess={t('hub.card.verified', 'Verified')} sx={{ fontSize: 16, color: 'var(--hub-accent)', ml: 0.5, verticalAlign: '-2px' }} />}
        </h3>
        {card.groupCode && <span className={`hub-pill ${card.groupCode}`}>{card.groupName ?? card.groupCode}</span>}
      </div>
      {subtitle && <span className="hub-muted">{subtitle}</span>}
      {!compact && card.tagline && <span className="hub-card-line">{card.tagline}</span>}
      {!compact && facts.length > 0 && <div className="hub-card-facts">{facts.map((fact) => <span key={String(fact)}>{fact}</span>)}</div>}
      {!compact && <div className="actions">
        <Link className="hub-btn sm" to={companyPath(card.slug)} onClick={(event) => openCompany(event, card.slug)}>
          {t('hub.card.open', 'Open')} {card.hasCustomUrl ? <NorthEastIcon sx={{ fontSize: 15 }} aria-hidden /> : <ArrowForwardIcon sx={{ fontSize: 15 }} aria-hidden />}
        </Link>
      </div>}
    </div>
  </article>;
}

/** A product tile; it opens the business page, where the product's own site takes over. */
export function ProductCardView({ product }: { product: ProductCard }) {
  const lang = usePublicLanguage();
  const { business } = product;
  const discounted = product.compareAtPrice != null && product.compareAtPrice > product.price;
  return <article className="hub-card hub-item">
    <div className="hub-item-photo">
      {product.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" decoding="async" /> : <span className="initials" aria-hidden>{initials(product.name)}</span>}
      {business.groupCode && <span className={`hub-pill ${business.groupCode}`}>{business.groupName ?? business.groupCode}</span>}
    </div>
    <div className="pad">
      <h3><Link to={companyPath(business.slug)} onClick={(event) => openCompany(event, business.slug)}>{product.name}</Link></h3>
      <span className="hub-muted">{[business.name, business.city].filter(Boolean).join(' · ')}</span>
      <span className="hub-item-price">
        {formatMoney(product.price, product.currency, lang)}
        {discounted && <s>{formatMoney(product.compareAtPrice, product.currency, lang)}</s>}
      </span>
    </div>
  </article>;
}

export function employmentLabel(t: (key: string, fallback: string) => string, type: string) {
  const labels: Record<string, string> = {
    FullTime: t('hub.jobs.type.FullTime', 'Full-time'),
    PartTime: t('hub.jobs.type.PartTime', 'Part-time'),
    Freelance: t('hub.jobs.type.Freelance', 'Freelance'),
    ChairRental: t('hub.jobs.type.ChairRental', 'Chair rental'),
    Seasonal: t('hub.jobs.type.Seasonal', 'Seasonal'),
    Internship: t('hub.jobs.type.Internship', 'Internship'),
  };
  return labels[type] ?? type;
}

export function JobCardView({ job }: { job: JobCard }) {
  const { t } = useTranslation();
  return <article className="hub-card hub-job">
    <span className="hub-job-icon" aria-hidden><WorkOutlineIcon sx={{ fontSize: 22 }} /></span>
    <div className="pad">
      <h3><Link to={ROUTES.job.replace(':jobId', job.id)}>{job.title}</Link></h3>
      <span className="hub-muted">{job.organizationName} · {job.city}</span>
      <span><span className="hub-pill job">{employmentLabel(t, job.employmentType)}</span></span>
    </div>
  </article>;
}

export function LoadingGrid({ count = 4, label, rail }: { count?: number; label: string; rail?: string }) {
  return <div className={rail ? `hub-rail ${rail}` : 'hub-grid'} role="status" aria-label={label}>
    {Array.from({ length: count }, (_, index) => <div key={index} className="hub-skeleton" />)}
  </div>;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return <div className="hub-error" role="alert">
    <span className="hub-error-icon" aria-hidden><ErrorOutlineIcon sx={{ fontSize: 22 }} /></span>
    <span>{message}</span>
    <button type="button" className="hub-btn sm ghost" onClick={onRetry}><RefreshIcon sx={{ fontSize: 16 }} aria-hidden />{t('hub.retry', 'Retry')}</button>
  </div>;
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="hub-empty"><InboxOutlinedIcon sx={{ fontSize: 22 }} aria-hidden /><span>{children}</span></div>;
}

/**
 * "What / where" search with type-ahead (GET public/organizations/suggest). Submitting goes to
 * `/search?q=&city=`; picking a suggestion goes straight to the company, category or city page.
 */
export function HubSearchBar({ initialQuery = '', initialCity = '', large = false, onSearch }: {
  initialQuery?: string; initialCity?: string; large?: boolean;
  /** Filter in place (the explore landing) instead of navigating to `/search`. */
  onSearch?: (search: { q: string; city: string }) => void;
}) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [city, setCity] = useState(initialCity);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLFormElement>(null);

  useEffect(() => setQuery(initialQuery), [initialQuery]);
  useEffect(() => setCity(initialCity), [initialCity]);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setSuggestions(null); return; }
    let active = true;
    const timer = window.setTimeout(() => {
      publicApi.suggest(lang, text).then((data) => { if (active) setSuggestions(data); }).catch(() => { if (active) setSuggestions(null); });
    }, 200);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query, lang]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function submit(event: FormEvent) {
    event.preventDefault();
    setOpen(false);
    if (onSearch) { onSearch({ q: query.trim(), city: city.trim() }); return; }
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (city.trim()) params.set('city', city.trim());
    const search = params.toString();
    navigate(`${ROUTES.search}${search ? `?${search}` : ''}`);
  }

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  const hasSuggestions = !!suggestions && (suggestions.organizations.length + suggestions.categories.length + suggestions.cities.length) > 0;

  return <form ref={wrapper} className="hub-search" role="search" onSubmit={submit}>
    <label className="hub-sr" htmlFor={large ? 'hub-q-hero' : 'hub-q'}>{t('hub.search.what', 'Business, service or category')}</label>
    <input id={large ? 'hub-q-hero' : 'hub-q'} value={query} autoComplete="off"
      placeholder={t('hub.search.what', 'Business, service or category')}
      onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
      onFocus={() => setOpen(true)}
      onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }} />
    <label className="hub-sr" htmlFor={large ? 'hub-city-hero' : 'hub-city'}>{t('hub.search.where', 'City')}</label>
    <input id={large ? 'hub-city-hero' : 'hub-city'} value={city} placeholder={t('hub.search.where', 'City')} onChange={(event) => setCity(event.target.value)} />
    <button type="submit">{t('hub.search.submit', 'Search')}</button>
    {open && hasSuggestions && <div className="hub-suggest">
      {suggestions!.organizations.map((item) => <Link to={companyPath(item.slug)} key={`o-${item.slug}`} onClick={(event) => { setOpen(false); openCompany(event, item.slug); }}>
        <span>{item.name}</span><small>{item.city}</small>
      </Link>)}
      {suggestions!.categories.map((item) => <button type="button" key={`c-${item.code}`} onClick={() => go(ROUTES.category.replace(':categoryCode', item.code.toLowerCase()))}>
        <span>{item.name}</span><small>{t('hub.search.category', 'Category')}</small>
      </button>)}
      {suggestions!.cities.map((item) => <button type="button" key={`city-${item}`} onClick={() => go(ROUTES.city.replace(':city', encodeURIComponent(item)))}>
        <span>{item}</span><small>{t('hub.search.city', 'City')}</small>
      </button>)}
    </div>}
  </form>;
}

/**
 * Theme picker shared by the public Hub, the customer area and the workspace chrome. Reads the
 * root AppThemeProvider; renders nothing when a test mounts a page without it.
 */
export function HubThemeMenu() {
  const { t } = useTranslation();
  const appTheme = useOptionalAppTheme();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(event: MouseEvent) {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!appTheme) return null;
  const label = (name: string) => t(`hub.theme.${name}`, name.replace('-', ' '));

  return <div className="hub-theme" ref={wrapper}>
    <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={t('hub.nav.theme', 'Theme')} onClick={() => setOpen((value) => !value)}>
      <PaletteOutlinedIcon sx={{ fontSize: 17 }} aria-hidden /> <span>{label(appTheme.theme)}</span>
    </button>
    {open && <div className="hub-theme-menu" role="menu">
      <ThemePicker value={appTheme.theme} onChange={appTheme.setTheme} renderOption={(name) => <button key={name} type="button" role="menuitemradio" aria-checked={name === appTheme.theme}
        onClick={() => { appTheme.setTheme(name); setOpen(false); }}>
        <span className={`hub-theme-dot ${name}`} aria-hidden /> {label(name)}
      </button>} />
    </div>}
  </div>;
}

function HubLanguageMenu({ value, onChange }: { value: string; onChange: (language: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    wrapper.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
    const outside = (event: MouseEvent) => { if (!wrapper.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  const names: Record<string, string> = { al: 'Shqip', en: 'English', it: 'Italiano' };
  return <div className="hub-theme hub-language-menu" ref={wrapper}>
    <button type="button" ref={trigger} aria-label={t('hub.nav.language', 'Language')} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((current) => !current)} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}>
      {value.toUpperCase()}
    </button>
    {open && <div className="hub-theme-menu" role="menu" aria-label={t('hub.nav.language', 'Language')} onKeyDown={(event) => {
      const options = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
      const index = options.indexOf(document.activeElement as HTMLButtonElement);
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options[next]?.focus();
      }
      if (event.key === 'Tab') setOpen(false);
    }}>
      {HUB_LANGUAGES.map((code) => <button type="button" key={code} role="menuitemradio" aria-checked={value === code} onClick={() => { onChange(code); setOpen(false); trigger.current?.focus(); }}>
        <span className="hub-language-code">{code.toUpperCase()}</span>{names[code]}
        {value === code && <span className="hub-language-check" aria-hidden>{'\u2713'}</span>}
      </button>)}
    </div>}
  </div>;
}

/** BCP 47 code for <html lang>: the API's `al` is Albanian, whose language tag is `sq`. */
function htmlLang(code: string) {
  return code === 'al' ? 'sq' : code;
}

/** Public header + footer around every Hub page. Works without a session. */
export function PublicLayout(props: { children: ReactNode; showHeaderSearch?: boolean; applicationNavigation?: ReactNode }) {
  return <PlatformCartProvider><PublicLayoutContent {...props} /></PlatformCartProvider>;
}
function PublicLayoutContent({ children, showHeaderSearch = true, applicationNavigation }: { children: ReactNode; showHeaderSearch?: boolean; applicationNavigation?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const lang = usePublicLanguage();
  const sessionStatus = useAppSelector((state) => state.session.status);
  const signedIn = sessionStatus === 'authenticated';
  const { pathname } = useLocation();
  const compactMenuRef = useRef<HTMLDivElement>(null);
  const mobileSystemRef = useRef<HTMLDivElement>(null);
  const [mobileSystemOpen, setMobileSystemOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [mobileNavigation, setMobileNavigation] = useState(() => window.matchMedia?.('(max-width: 600px)').matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 600px)');
    if (!media) return;
    const update = () => setMobileNavigation(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => { if (!menuOpen) { setSystemOpen(false); setApplicationOpen(false); } }, [menuOpen]);

  useEffect(() => { document.documentElement.lang = htmlLang(lang); }, [lang]);
  // A route change always closes the mobile menu; Escape closes it too.
  useEffect(() => { setMenuOpen(false); setMobileSystemOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen && !mobileSystemOpen) return undefined;
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') { setMenuOpen(false); setMobileSystemOpen(false); } }
    function onOutsideClick(event: MouseEvent) {
      if (event.target instanceof Node) {
        if (!compactMenuRef.current?.contains(event.target)) setMenuOpen(false);
        if (!mobileSystemRef.current?.contains(event.target)) setMobileSystemOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutsideClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onOutsideClick); };
  }, [menuOpen, mobileSystemOpen]);

  function changeLanguage(next: string) {
    try { localStorage.setItem('i18nextLng', next); } catch { /* storage may be blocked */ }
    void i18n?.changeLanguage?.(next);
  }

  const languageSwitch = <div className="hub-lang" role="group" aria-label={t('hub.nav.language', 'Language')}>
    {HUB_LANGUAGES.map((code) => <button key={code} type="button" aria-pressed={code === lang} onClick={() => changeLanguage(code)}>{code.toUpperCase()}</button>)}
  </div>;

  const systemOptions = <>
            <nav aria-label={t('hub.nav.system', 'System')}>
              <NavLink to={ROUTES.businesses}><StorefrontIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.businesses', 'Businesses')}</NavLink>
              <NavLink to={ROUTES.products}><AppsIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.products', 'Products')}</NavLink>
              <NavLink to={ROUTES.jobs}><WorkOutlineIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.jobs', 'Jobs')}</NavLink>
              <NavLink to={ROUTES.social}><DynamicFeedIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.social', 'Social')}</NavLink>
              <NavLink to={ROUTES.forBusiness}><StorefrontIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.forBusiness', 'For business')}</NavLink>
              {sessionStatus === 'initializing' ? <AccountLoading /> : signedIn
                ? <>
                  <NavLink to={ROUTES.switchApp}><SwapHorizIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.switch', 'Switch app')}</NavLink>
                </>
                : <>
                  <NavLink to={ROUTES.login}><LoginIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.signIn', 'Sign in')}</NavLink>
                  <NavLink to={ROUTES.register}><PersonOutlineIcon sx={{ fontSize: 20 }} aria-hidden />{t('hub.nav.register', 'Join')}</NavLink>
                </>}
            </nav>
  </>;

  return <div className="hub">
    <a className="skip-link" href="#hub-main">{t('hub.nav.skip', 'Skip to content')}</a>
    <header className="hub-top">
      <div className="hub-wrap">
        <Link to={ROUTES.landing} className="hub-logo" aria-label={t('hub.home', 'Biznesi home')}>Biznesi</Link>
        {showHeaderSearch && <HubSearchBar />}
        <nav className="hub-nav" aria-label={t('hub.nav.label', 'Main')}>
          {/* The three main sections are plain links on every public page: they are what search
              engines read as the site's structure (sitelinks). Social lives in the footer. */}
          <NavLink to={ROUTES.businesses} className="hub-nav-link">{t('hub.nav.businesses', 'Businesses')}</NavLink>
          <NavLink to={ROUTES.products} className="hub-nav-link">{t('hub.nav.products', 'Products')}</NavLink>
          <NavLink to={ROUTES.jobs} className="hub-nav-link">{t('hub.nav.jobs', 'Jobs')}</NavLink>
          <NavLink to={ROUTES.forBusiness} className="hub-nav-link">{t('hub.nav.forBusiness', 'For business')}</NavLink>
          <span className="hub-nav-sep" aria-hidden />
          {languageSwitch}
          <HubThemeMenu />
          <CartButton />
          {signedIn && <NotificationBell />}
          {sessionStatus === 'initializing' ? <AccountLoading /> : signedIn
            ? <>
              <Link to={ROUTES.switchApp} className="hub-nav-link">{t('hub.nav.switch', 'Switch app')}</Link>
              <Link className="hub-btn sm" to={ROUTES.personalAccount}><PersonOutlineIcon sx={{ fontSize: 18 }} aria-hidden />{t('hub.nav.account', 'My account')}</Link>
            </>
            : <>
              <Link to={ROUTES.login} className="hub-nav-link">{t('hub.nav.signIn', 'Sign in')}</Link>
              <Link className="hub-btn sm" to={ROUTES.register}>{t('hub.nav.register', 'Join')}</Link>
            </>}
        </nav>
        <div className="hub-top-compact" role="navigation" aria-label={t('hub.nav.mobileNavigation', 'Mobile navigation')}>
          <div className="hub-bottom-navigation">
          <div className="hub-bottom-system" ref={mobileSystemRef}>
            <button type="button" className="hub-bottom-link" aria-expanded={mobileSystemOpen} aria-controls="hub-bottom-system-options" onClick={() => { setMobileSystemOpen((open) => !open); setMenuOpen(false); }}><SettingsOutlinedIcon aria-hidden /><span>{t('hub.nav.system', 'System')}</span></button>
            <div id="hub-bottom-system-options" className="hub-mobile-menu hub-bottom-system-options" data-open={mobileSystemOpen} hidden={!mobileSystemOpen}><div className="hub-wrap hub-menu-content">{systemOptions}</div></div>
          </div>
          <NavLink to={ROUTES.search} className="hub-bottom-link"><SearchIcon aria-hidden /><span>{t('hub.nav.mobileSearch', 'Search')}</span></NavLink>
          <NavLink to={ROUTES.landing} end className="hub-bottom-link"><HomeOutlinedIcon aria-hidden /><span>{t('hub.nav.mobileHome', 'Home')}</span></NavLink>
          <CartButton />
          <NavLink to={signedIn ? ROUTES.personalAccount : ROUTES.login} className="hub-bottom-link"><PersonOutlineIcon aria-hidden /><span>{t('hub.nav.mobileAccount', 'Account')}</span></NavLink>
          </div>
          <div className="hub-header-preferences">
            {signedIn && <NotificationBell />}
            <HubLanguageMenu value={lang} onChange={changeLanguage} />
            <HubThemeMenu />
          </div>
          <div className="hub-compact-menu-anchor" data-has-application={Boolean(applicationNavigation)} ref={compactMenuRef}>
          <button type="button" className="hub-icon-btn" aria-expanded={menuOpen} aria-controls="hub-mobile-menu"
            aria-label={menuOpen ? t('hub.nav.closeMenu', 'Close menu') : t('hub.nav.openMenu', 'Open menu')}
            onClick={() => { setMenuOpen((value) => !value); setMobileSystemOpen(false); }}>
            {menuOpen ? <CloseIcon sx={{ fontSize: 22 }} aria-hidden /> : <MenuIcon sx={{ fontSize: 22 }} aria-hidden />}
            <span className="hub-tablet-menu-label">{t('hub.nav.menu', 'Menu')}</span><span className="hub-mobile-menu-label">{t('hub.nav.application', 'Application')}</span>
          </button>
      <div id="hub-mobile-menu" className="hub-mobile-menu" data-open={menuOpen} hidden={!menuOpen}>
        <div className="hub-wrap">
          <section className="hub-menu-section hub-top-system-section" aria-labelledby="hub-system-heading">
            <h2 id="hub-system-heading" className="hub-menu-heading"><button type="button" aria-expanded={systemOpen} aria-controls="hub-system-options" onClick={() => setSystemOpen((open) => !open)}>{t('hub.nav.system', 'System')}<span aria-hidden>{systemOpen ? '\u2212' : '+'}</span></button></h2>
            <div id="hub-system-options" className="hub-menu-content" hidden={!systemOpen}>
              {systemOptions}
            </div>
          </section>
          {applicationNavigation && <section className="hub-menu-section" aria-labelledby="hub-application-heading">
            <h2 id="hub-application-heading" className="hub-menu-heading" hidden={mobileNavigation}><button type="button" aria-expanded={applicationOpen} aria-controls="hub-application-options" onClick={() => setApplicationOpen((open) => !open)}>{t('hub.nav.application', 'Application')}<span aria-hidden>{applicationOpen ? '\u2212' : '+'}</span></button></h2>
            <div id="hub-application-options" className="hub-compact-application" hidden={!mobileNavigation && !applicationOpen}>{applicationNavigation}</div>
          </section>}
        </div>
      </div>
          </div>
        </div>
      </div>

    </header>
    <main id="hub-main" tabIndex={-1}>{children}</main>
    <footer className="hub-foot">
      <div className="hub-wrap hub-foot-columns">
        <div className="hub-foot-about">
          <span className="hub-logo">Biznesi</span>
          <p>{t('hub.seo.footer.about', 'The directory and platform for businesses in Albania: find, order, book and get hired.')}</p>
        </div>
        <nav aria-label={t('hub.nav.businesses', 'Businesses')}>
          <h2>{t('hub.nav.businesses', 'Businesses')}</h2>
          <Link to={ROUTES.businesses}>{t('hub.search.title', 'All businesses')}</Link>
          {FOOTER_CATEGORIES.map((code) => <Link key={code} to={ROUTES.category.replace(':categoryCode', code.toLowerCase())}>{t(`hub.seo.footer.categories.${code}`, code)}</Link>)}
        </nav>
        <nav aria-label={t('hub.nav.products', 'Products')}>
          <h2>{t('hub.nav.products', 'Products')}</h2>
          <Link to={ROUTES.products}>{t('hub.products.all', 'All products')}</Link>
          {PRODUCTS.map((product) => <Link key={product.slug} to={ROUTES.product.replace(':product', product.slug)}>{t(`hub.products.items.${product.key}.name`, product.key)}</Link>)}
        </nav>
        <nav aria-label={t('hub.nav.footer', 'Footer')}>
          <h2>{t('hub.seo.footer.explore', 'Biznesi')}</h2>
          <Link to={ROUTES.jobs}>{t('hub.nav.jobs', 'Jobs')}</Link>
          <Link to={ROUTES.social}>{t('hub.nav.social', 'Social')}</Link>
          <Link to={ROUTES.forBusiness}>{t('hub.nav.forBusiness', 'For business')}</Link>
          <Link to={ROUTES.register}>{t('hub.nav.register', 'Join')}</Link>
        </nav>
      </div>
      <div className="hub-wrap hub-foot-bottom">
        <span className="hub-foot-copy">© {new Date().getFullYear()} Biznesi · biznesi.al</span>
      </div>
    </footer>
  </div>;
}
